import React, { ReactNode } from "react";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import styled from "@emotion/styled";
import { LoadingPlaceHolder } from "../progress/LoadingPlaceHolder";
import { ChartMenu } from "./ChartMenu";
import { ToolTipInfo } from "./ToolTipInfo";

interface Props {
  className?: string;
  isFetching?: boolean;
  title: string;
  onHide?: () => void;
  children: ReactNode;
  toolTipData?: {
    desc1: string;
    desc2?: string;
  };
  customActions?: ReactNode;
}

const StyledPaper = styled(Paper)`
  &.MuiPaper-root {
    background-color: ${({ theme }) => theme.palette.background.default};
    position: relative;
    align-items: flex-start;
    padding-top: ${({ theme }) => theme.spacing(1)};
    padding-bottom: ${({ theme }) => theme.spacing(1)};
    box-shadow: ${({ theme }) => theme.shadows[2]};
    border-radius: 10px;
  }
  height: 100%;
  width: 100%;
`;

const Head = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  padding: theme.spacing(2, 4, 2),
}));

const ActionsContainer = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
});

const TitleContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
});

export const DataBlockLayout = ({
  isFetching,
  title,
  onHide,
  children,
  customActions,
  toolTipData,
  className,
}: Props) => {
  const theme = useTheme();
  const titleColor = theme.palette.text.secondary;
  const iconColor = theme.palette.icon.secondary;

  if (isFetching) {
    return <LoadingPlaceHolder dataTestId="data-block-loading" />;
  }

  return (
    <StyledPaper elevation={3} square={false} className={className}>
      <Head>
        <TitleContainer>
          <Typography
            data-testid="title"
            variant="body2"
            color={titleColor}
            sx={{
              marginRight: toolTipData ? 1 : 0,
            }}
          >
            {title}
          </Typography>

          {toolTipData && (
            <ToolTipInfo {...toolTipData} iconColor={iconColor} />
          )}
        </TitleContainer>

        {customActions && <ActionsContainer>{customActions}</ActionsContainer>}

        {onHide && (
          <ActionsContainer>
            <ChartMenu
              onHide={() => {
                onHide();
              }}
            />
          </ActionsContainer>
        )}
      </Head>
      {children}
    </StyledPaper>
  );
};
